import { createRootRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import ErrorPageLayout from '@/components/ui/error-page-layout';
import { useEffect } from 'react';
import { Box } from '@/components/ui/lib/box';
import { Button } from '@/components/ui/lib/button';
import { HStack } from '@/components/ui/lib/hstack';
import Navbar from '@/app/components/navbar';
import RouteProgressBar from '@/app/components/route-progress-bar';
import Footer from '@/app/components/footer';
import { APP_NAME } from '@/config/constants';
import { DICTIONARY } from '@/config/dictionary';
import { useAppStore } from '@/stores/app-store';
import { isDev } from '@/config/env';
import { useSessionTimeout } from '@/hooks/use-session-timeout';
import { useRoutePending } from '@/hooks/use-route-pending';
import { useBootstrapAuth } from '@/hooks/use-bootstrap-auth';
import { useAuthExpired } from '@/hooks/use-auth-expired';

const RootLayout = () => {
  useSessionTimeout();
  useBootstrapAuth();
  useAuthExpired();
  const colorMode = useAppStore((s) => s.colorMode);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const showGlobalShell = pathname === '/';
  // Route loaders await their prefetches, so a page swap can sit for a beat with the
  // outgoing page still on screen. The bar covers that beat; past 200ms the router swaps in
  // the skeletons from `route-pending.tsx` and the bar stays up alongside them.
  const isRoutePending = useRoutePending();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', colorMode === 'dark');
  }, [colorMode]);

  return (
    <Box
      display="flex"
      minH="100vh"
      flexDirection="column"
      className="bg-page text-base"
      data-app={APP_NAME}
    >
      <RouteProgressBar active={isRoutePending} />
      {showGlobalShell ? <Navbar /> : null}
      <Box as="main" flex="1" aria-busy={isRoutePending}>
        <Outlet />
      </Box>
      {showGlobalShell ? <Footer /> : null}
    </Box>
  );
};

const NotFound = () => {
  const router = useRouter();
  return (
    <ErrorPageLayout
      fullscreen
      heading={DICTIONARY.errors.notFound.heading}
      subheading={DICTIONARY.errors.notFound.subheading}
      description={DICTIONARY.errors.notFound.body}
      actions={
        <HStack gap="3" justify="center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              router.history.back();
            }}
          >
            {DICTIONARY.errors.goBack}
          </Button>
        </HStack>
      }
    />
  );
};

const RootError = ({ error }: { error?: unknown }) => {
  const router = useRouter();
  const devMessage = isDev
    ? error instanceof Error
      ? error.message
      : JSON.stringify(error, null, 2)
    : null;
  if (isDev) console.error('[root error]', error);
  return (
    <ErrorPageLayout
      fullscreen
      heading={DICTIONARY.errors.pageError}
      description={DICTIONARY.errors.pageErrorDescription}
      devMessage={devMessage}
      actions={
        <HStack gap="3" justify="center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              router.history.back();
            }}
          >
            {DICTIONARY.errors.goBack}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void router.invalidate();
            }}
          >
            {DICTIONARY.errors.retry}
          </Button>
        </HStack>
      }
    />
  );
};

export const ErrorComponent = RootError;

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});

export default RootLayout;
